// src/hooks/useProjectManagement.js
import { useCallback } from 'react';
import api from '../api';

/**
 * 图板 CRUD + 封面设置
 *
 * @param {object} deps
 * @param {string}   deps.projectId
 * @param {Function} deps.setAllProjects
 * @param {Function} deps.setProjectName
 * @param {Function} deps.setTempName
 * @param {Function} deps.setAlertMsg
 * @param {Function} deps.setCoverCropUrl
 * @param {Function} deps.setCoverProject
 * @param {Function} deps.setProjectImages
 * @param {Function} deps.setShowImagePicker
 * @param {Function} deps.navigate
 */
const useProjectManagement = (deps) => {
  const {
    projectId, setAllProjects, setProjectName, setTempName, setAlertMsg,
    setCoverCropUrl, setCoverProject, setProjectImages, setShowImagePicker,
    navigate,
  } = deps;

  // 新建图板
  const handleCreateProject = useCallback(async () => {
    try {
      const data = await api.post(`/api/projects`, { name: "未命名创意空间" });
      if (data.success && data.id) {
        navigate(`/editor/${data.id}`);
      }
    } catch (e) {
      console.error('创建图板失败:', e);
      setAlertMsg({ text: "创建图板失败", type: "error" });
    }
  }, [navigate, setAlertMsg]);

  // 重命名图板
  const handleRenameProject = useCallback(async (id, newName) => {
    try {
      await api.put(`/api/projects/${id}`, { name: newName });
      setAllProjects(prev => prev.map(p => p.id === id ? { ...p, name: newName } : p));
      if (id === parseInt(projectId)) {
        setProjectName(newName);
        setTempName(newName);
      }
      setAlertMsg({ text: "重命名成功", type: "success" });
    } catch (e) {
      console.error('重命名失败:', e);
      setAlertMsg({ text: "重命名失败", type: "error" });
    }
  }, [projectId, setAllProjects, setProjectName, setTempName, setAlertMsg]);

  // 删除图板
  const handleDeleteProject = useCallback(async (id) => {
    if (id === parseInt(projectId)) {
      setAlertMsg({ text: "不能删除当前正在编辑的图板", type: "warning" });
      return;
    }
    try {
      await api.delete(`/api/projects/${id}`);
      setAllProjects(prev => prev.filter(p => p.id !== id));
      // 不删除本地缓存文件 — 文件在删除项目时按文件夹整体清理
      setAlertMsg({ text: "图板已删除", type: "success" });
    } catch (e) {
      console.error('删除图板失败:', e);
      setAlertMsg({ text: "删除失败", type: "error" });
    }
  }, [projectId, setAllProjects, setAlertMsg]);

  // 设置图板封面 — 打开图片选择器
  const handleSetProjectCover = useCallback(async (project) => {
    setCoverProject(project);
    try {
      const data = await api.get(`/api/projects/${project.id}`);
      const nodes = data.nodes || [];
      // 🆕 优先使用本地缓存的图片，fallback 到远程 URL
      const images = nodes
        .filter(n => n.data?.type === 'image' && (n.data?.localCacheUrl || n.data?.output))
        .map(n => ({ url: n.data.localCacheUrl || n.data.output, title: n.data.title }));
      setProjectImages(images);
      setShowImagePicker(true);
    } catch (e) {
      console.error('加载项目图片失败:', e);
      setAlertMsg({ text: "加载图片失败", type: "error" });
    }
  }, [setCoverProject, setProjectImages, setShowImagePicker, setAlertMsg]);

  // 选择图片后进入裁剪
  const handleSelectCoverImage = useCallback((url) => {
    setShowImagePicker(false);
    setCoverCropUrl(url);
  }, [setShowImagePicker, setCoverCropUrl]);

  // 当前项目封面保存
  const handleCoverSave = useCallback(async (blob) => {
    const formData = new FormData();
    const fileName = `cover-${projectId}-${Date.now()}.jpg`;
    formData.append('file', blob, fileName);
    try {
      const uploadData = await api.upload(`/api/upload?type=thumbnail`, formData);
      if (uploadData.url) {
        await api.put(`/api/projects/${projectId}`, { thumbnail: uploadData.url });
        setAllProjects(prev => prev.map(p =>
          p.id === parseInt(projectId) ? { ...p, thumbnail: uploadData.url } : p
        ));
        setAlertMsg({ text: "封面设置成功", type: "success" });
      }
    } catch (e) {
      console.error('保存封面失败:', e);
      setAlertMsg({ text: "封面设置失败", type: "error" });
    }
    setCoverCropUrl(null);
  }, [projectId, setAllProjects, setAlertMsg, setCoverCropUrl]);

  // 其他项目封面保存
  const handleOtherProjectCoverSave = useCallback(async (blob) => {
    const coverProject = deps.coverProject;
    if (!coverProject) {
      setCoverCropUrl(null);
      return;
    }
    const formData = new FormData();
    const fileName = `cover-${coverProject.id}-${Date.now()}.jpg`;
    formData.append('file', blob, fileName);
    try {
      const uploadData = await api.upload('/api/upload?type=thumbnail', formData);
      if (uploadData.url) {
        await api.put(`/api/projects/${coverProject.id}`, { thumbnail: uploadData.url });
        setAllProjects(prev => prev.map(p =>
          p.id === coverProject.id ? { ...p, thumbnail: uploadData.url } : p
        ));
        setAlertMsg({ text: "封面设置成功", type: "success" });
      }
    } catch (e) {
      console.error('保存封面失败:', e);
      setAlertMsg({ text: "封面设置失败", type: "error" });
    }
    setCoverCropUrl(null);
    setCoverProject(null);
    setProjectImages([]);
  }, [deps.coverProject, setAllProjects, setAlertMsg, setCoverCropUrl, setCoverProject, setProjectImages]);

  // 关闭封面弹窗
  const handleCloseCoverModals = useCallback(() => {
    setShowImagePicker(false);
    setCoverCropUrl(null);
    setCoverProject(null);
    setProjectImages([]);
  }, [setShowImagePicker, setCoverCropUrl, setCoverProject, setProjectImages]);

  return {
    handleCreateProject, handleRenameProject, handleDeleteProject,
    handleSetProjectCover, handleSelectCoverImage,
    handleCoverSave, handleOtherProjectCoverSave, handleCloseCoverModals,
  };
};

export default useProjectManagement;
